import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  Grid,
  Chip,
  Autocomplete,
  Popper,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useApi } from '../../api/ApiProvider';
import { useToastMessageQueue } from '../ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import ColorPicker from '../ColorPicker';
import { WorkflowStateDTO } from '../../axiosApi/models';
import { PermissionsGate } from '../authorization/PermissionsGate';
import { SCOPES, ROLES } from 'common/permissions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import Spinner from 'components/Spinner';

interface FormData {
  name: string;
  defaultDays: number | '';
  color: string;
  validityType: 'year' | 'quantity' | 'period';
  quantityDays: number | '';
  quantityStartDate: Date | null;
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  isBillable: boolean;
  hasNegativeTime: boolean;
  isAccumulative: boolean;
  accumulativeDays: number | '';
  workflowStateIds: string[];
}

const NewRegimeType: React.FC = () => {
  const { regimeId } = useParams<{ regimeId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const toast = useToastMessageQueue();

  const [regimeName, setRegimeName] = useState<string>('');
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    defaultDays: '',
    color: '#1976d2',
    validityType: 'year',
    quantityDays: '',
    quantityStartDate: null,
    dateRange: {
      startDate: null,
      endDate: null,
    },
    isBillable: false,
    hasNegativeTime: false,
    isAccumulative: false,
    accumulativeDays: '',
    workflowStateIds: [],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [workflowStates, setWorkflowStates] = useState<WorkflowStateDTO[]>([]);
  const [workflowStatesLoading, setWorkflowStatesLoading] = useState<boolean>(false);

  useEffect(() => {
    if (regimeId) {
      fetchRegimeName();
    }
    fetchWorkflowStates();
  }, [regimeId]);

  const fetchRegimeName = async () => {
    if (!regimeId) return;
    try {
      const response = await api.timeoffApi.apiVversionTimeOffRegimeIdGet(
        parseInt(regimeId),
        "1"
      );
      if (response.status === 200 && response.data?.data) {
        setRegimeName(response.data.data.name || `Regime ${regimeId}`);
      }
    } catch (error) {
      console.error("Error fetching regime details:", error);
      setRegimeName(`Regime ${regimeId}`);
    }
  };

  const fetchWorkflowStates = async () => {
    try {
      setWorkflowStatesLoading(true);
      const response = await api.workflowApi.apiVversionWorkflowStatesByModuleNameModuleNameGet(
        "TimeOff",
        "1"
      );
      if (response.status === 200 && response.data?.data) {
        const states = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data.queryResult || [];
        setWorkflowStates(states);
      }
    } catch (error) {
      console.error("Error fetching workflow states:", error);
      setWorkflowStates([]);
    } finally {
      setWorkflowStatesLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.type === 'number'
      ? event.target.value === '' ? '' : Number(event.target.value)
      : event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({
      ...prev,
      color
    }));
  };

  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => {
    setFormData(prev => ({
      ...prev,
      dateRange: {
        startDate: range.from,
        endDate: range.to,
      }
    }));

    if (errors.dateRange) {
      setErrors(prev => ({
        ...prev,
        dateRange: ''
      }));
    }
  };

  const handleQuantityStartDateChange = (date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      quantityStartDate: date
    }));

    if (errors.quantityStartDate) {
      setErrors(prev => ({
        ...prev,
        quantityStartDate: ''
      }));
    }
  };

  const handleWorkflowStateChange = (event: any, newValue: WorkflowStateDTO[]) => {
    setFormData(prev => ({
      ...prev,
      workflowStateIds: newValue.map(state => state.id?.toString() || '')
    }));

    if (errors.workflowStates) {
      setErrors(prev => ({
        ...prev,
        workflowStates: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = intl.get('validate.errors.isRequired') || 'This field is required';
    }

    if (formData.defaultDays === '' || formData.defaultDays === 0 || (typeof formData.defaultDays === 'number' && formData.defaultDays <= 0)) {
      newErrors.defaultDays = intl.get('timeOff.newRegimeType.errors.defaultDaysRequired') || 'Default days is required';
    }

    if (formData.workflowStateIds.length === 0) {
      newErrors.workflowStates = intl.get('timeOff.newRegimeType.errors.workflowStatesRequired') || 'At least one workflow state is required';
    }

    if (formData.validityType === 'quantity') {
      if (formData.quantityDays === '' || formData.quantityDays === 0 || (typeof formData.quantityDays === 'number' && formData.quantityDays <= 0)) {
        newErrors.quantityDays = intl.get('timeOff.newRegimeType.errors.quantityDaysRequired') || 'Quantity days is required';
      }
      if (!formData.quantityStartDate) {
        newErrors.quantityStartDate = intl.get('timeOff.newRegimeType.errors.quantityStartDateRequired') || 'Start date is required';
      }
    }

    if (formData.validityType === 'period') {
      if (!formData.dateRange.startDate || !formData.dateRange.endDate) {
        newErrors.dateRange = intl.get('timeOff.newRegimeType.errors.dateRangeRequired') || 'Both start and end dates are required';
      } else if (formData.dateRange.startDate >= formData.dateRange.endDate) {
        newErrors.dateRange = intl.get('timeOff.newRegimeType.errors.invalidDateRange') || 'End date must be after start date';
      }
    }

    if (formData.isAccumulative) {
      if (formData.accumulativeDays === '' || formData.accumulativeDays === 0 || (typeof formData.accumulativeDays === 'number' && formData.accumulativeDays <= 0)) {
        newErrors.accumulativeDays = intl.get('timeOff.newRegimeType.errors.accumulativeDaysRequired') || 'Accumulative days is required';
      }
    }

    if (formData.validityType === 'quantity' && formData.quantityStartDate && formData.quantityDays && formData.defaultDays) {
      const startDate = new Date(formData.quantityStartDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + Number(formData.quantityDays) - 1);

      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();

      if (startYear !== endYear) {
        newErrors.quantityDays = intl.get('timeOff.newRegimeType.errors.quantityDaysCrossYear') || 'Quantity days cannot span across multiple years';
      }

      if (Number(formData.defaultDays) > Number(formData.quantityDays)) {
        newErrors.defaultDays = intl.get('timeOff.newRegimeType.errors.defaultDaysExceedAvailable') || 'Default days cannot exceed available days';
      }
    }

    if (formData.validityType === 'period' && formData.dateRange.startDate && formData.dateRange.endDate && formData.defaultDays) {
      const startDate = new Date(formData.dateRange.startDate);
      const endDate = new Date(formData.dateRange.endDate);
      const periodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      if (Number(formData.defaultDays) > periodDays) {
        newErrors.defaultDays = intl.get('timeOff.newRegimeType.errors.defaultDaysExceedPeriod') || 'Default days cannot exceed the period length';
      }
    }

    // Yearly validation - check if default days exceed reasonable limit (365 days)
    if (formData.validityType === 'year' && formData.defaultDays) {
      if (Number(formData.defaultDays) > 365) {
        newErrors.defaultDays = intl.get('timeOff.newRegimeType.errors.defaultDaysExceedYear') || 'Default days cannot exceed 365 for yearly validity';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const mapValidityTypeToId = (validityType: 'year' | 'quantity' | 'period'): number => {
    switch (validityType) {
      case 'year':
        return 1;
      case 'quantity':
        return 2;
      case 'period':
        return 3;
      default:
        return 1;
    }
  };

    const isFormValid = (): boolean => {
      if (!formData.name.trim()) return false;
      if (formData.defaultDays === '' || formData.defaultDays === 0 || (typeof formData.defaultDays === 'number' && formData.defaultDays <= 0)) return false;
      if (formData.workflowStateIds.length === 0) return false;

      if (formData.validityType === 'quantity') {
        if (formData.quantityDays === '' || formData.quantityDays === 0 || (typeof formData.quantityDays === 'number' && formData.quantityDays <= 0)) return false;
        if (!formData.quantityStartDate) return false;
      }

      if (formData.isAccumulative) {
        if (formData.accumulativeDays === '' || formData.accumulativeDays === 0 || (typeof formData.accumulativeDays === 'number' && formData.accumulativeDays <= 0)) return false;
      }

      return true;
    };

  const handleSubmit = async () => {
    setCreateLoading(true);
    if (!validateForm()) {
      setCreateLoading(false);
      return;
    }

    try {
      const createTimeOffTypeCommand = {
        idTimeOffRegime: parseInt(regimeId || '0'),
        idValidityPeriodType: mapValidityTypeToId(formData.validityType),
        name: formData.name,
        defaultDays: Number(formData.defaultDays),
        allowNegativeBalance: formData.hasNegativeTime,
        ...(formData.validityType === 'quantity' && {
          availableDays: Number(formData.quantityDays),
          validFrom: formData.quantityStartDate,
          validUntil: new Date(formData.quantityStartDate.getFullYear(), formData.quantityStartDate.getMonth(), formData.quantityStartDate.getDate() + Number(formData.quantityDays) - 1)
        }),
        ...(formData.validityType === 'period' && {
          validFrom: formData.dateRange.startDate,
          validUntil: formData.dateRange.endDate
        }),
        ...(formData.validityType === 'year' && {
          validFrom: new Date(new Date().getFullYear(), 0, 1),
          validUntil: new Date(new Date().getFullYear(), 11, 31)
        }),
        isYearly: formData.validityType === 'year',
        isActive: true,
        isBillable: formData.isBillable,
        isCumulative: formData.isAccumulative,
        cumulativeDays: formData.isAccumulative ? Number(formData.accumulativeDays) : 0,
        color: formData.color,
        ...(formData.workflowStateIds.length > 0 && {
          idWorkflowStates: formData.workflowStateIds.map(id => Number(id))
        })
      };


      const response = await api.timeoffApi.apiVversionTimeOffTypePost(
        "1",
        createTimeOffTypeCommand
      );

      if (response.status === 200 || response.status === 201) {
        toast.success({
          message: intl.get('timeOff.newRegimeType.toast.success')
        });

        navigate(`/regime/${regimeId}/types`);
      } else {
        throw new Error('Failed to create time off type');
      }
    } catch (error) {
      console.error('Error creating time off type:', error);
      toast.error({
        message: 'Error creating time off type. Please try again.'
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/regime/${regimeId}/types`);
  };

  return (
    <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 2 }}>
          {intl.get('timeOff.newRegimeType.title')}
        </Typography>
        <Box>
          <PermissionsGate
            viewScopes={[SCOPES['timeoff.rrhh']]}
            editScopes={[SCOPES['timeoff.rrhh']]}
            viewRoles={[ROLES.employeeReader]}
            editRoles={[ROLES.employeeWriter]}
            RenderError={()=>{return <span>{intl.get('permissionsGate')}</span>}}
          >
          <Box sx={{p :3}}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={intl.get('timeOff.newRegimeType.form.regime')}
                  value={regimeName}
                  disabled
                  variant="outlined"
                  error={!!errors.regime}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={intl.get('timeOff.newRegimeType.form.name')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 40 },
                    }
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={intl.get('timeOff.newRegimeType.form.defaultDays')}
                  type="number"
                  value={formData.defaultDays}
                  onChange={handleInputChange('defaultDays')}
                  error={!!errors.defaultDays}
                  helperText={errors.defaultDays || intl.get('timeOff.newRegimeType.form.defaultDaysHelper')}
                  required
                  variant="outlined"
                  slotProps={{
                    input: {
                    inputProps: {
                        min: 1,
                        max: 999,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {intl.get('timeOff.newRegimeType.form.colorHelper')}
                    </Typography>
                    <ColorPicker color={formData.color} setColor={handleColorChange} />
                  </Box>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{intl.get('timeOff.newRegimeType.form.validity.title')}</FormLabel>
                  <RadioGroup
                    value={formData.validityType}
                    onChange={handleInputChange('validityType')}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="year"
                      control={<Radio />}
                      label={intl.get('timeOff.newRegimeType.form.validity.year')}
                    />
                    <FormControlLabel
                      value="quantity"
                      control={<Radio />}
                      label={intl.get('timeOff.newRegimeType.form.validity.quantity')}
                    />
                    <FormControlLabel
                      value="period"
                      control={<Radio />}
                      label={intl.get('timeOff.newRegimeType.form.validity.period')}
                    />
                  </RadioGroup>

                  {formData.validityType === 'quantity' && (
                    <Box sx={{ mt: 2, ml: 4 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <DatePicker
                          label={intl.get('timeOff.newRegimeType.form.startDate')}
                          value={formData.quantityStartDate}
                          onChange={handleQuantityStartDateChange}
                          slotProps={{
                            textField: {
                              error: !!errors.quantityStartDate,
                              helperText: errors.quantityStartDate,
                              size: 'small',
                              sx: { width: 200, minWidth: 200, maxWidth: 200 }
                            }
                          }}
                        />
                        <TextField
                          label={intl.get('timeOff.newRegimeType.form.days')}
                          type="number"
                          value={formData.quantityDays}
                          onChange={handleInputChange('quantityDays')}
                          error={!!errors.quantityDays}
                          helperText={errors.quantityDays}
                          slotProps={{
                            input: {
                            inputProps: {
                                min: 1,
                                max: 999,
                              },
                            },
                          }}
                          size="small"
                          sx={{ width: 200, minWidth: 200, maxWidth: 200 }}
                        />
                      </Box>
                    </Box>
                  )}

                  {formData.validityType === 'period' && (
                    <Box sx={{ mt: 2, ml: 4 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <DatePicker
                          label={intl.get('timeOff.newRegimeType.form.startDate')}
                          value={formData.dateRange.startDate}
                          onChange={(date) => handleDateRangeChange({ from: date, to: formData.dateRange.endDate })}
                          views={['month', 'day']}
                          format="MM/dd"
                          slotProps={{
                            textField: {
                              error: !!errors.dateRange,
                              size: 'small',
                              sx: { width: 200, minWidth: 200, maxWidth: 200 }
                            }
                          }}
                        />
                        <DatePicker
                          label={intl.get('timeOff.newRegimeType.form.endDate')}
                          value={formData.dateRange.endDate}
                          onChange={(date) => handleDateRangeChange({ from: formData.dateRange.startDate, to: date })}
                          views={['month', 'day']}
                          format="MM/dd"
                          slotProps={{
                            textField: {
                              error: !!errors.dateRange,
                              size: 'small',
                              sx: { width: 200, minWidth: 200, maxWidth: 200 }
                            }
                          }}
                        />
                      </Box>
                      {errors.dateRange && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          {errors.dateRange}
                        </Typography>
                      )}
                    </Box>
                  )}
                </FormControl>
              </Grid>

              <Grid size={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {intl.get('timeOff.newRegimeType.form.perks.title')}
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isBillable}
                        onChange={handleInputChange('isBillable')}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{intl.get('timeOff.newRegimeType.form.perks.isBillable')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {intl.get('timeOff.newRegimeType.form.perks.isBillableHelper')}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.hasNegativeTime}
                        onChange={handleInputChange('hasNegativeTime')}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{intl.get('timeOff.newRegimeType.form.perks.hasNegativeTime')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {intl.get('timeOff.newRegimeType.form.perks.hasNegativeTimeHelper')}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isAccumulative}
                        onChange={handleInputChange('isAccumulative')}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{intl.get('timeOff.newRegimeType.form.perks.isAccumulative')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {intl.get('timeOff.newRegimeType.form.perks.isAccumulativeHelper')}
                        </Typography>
                      </Box>
                    }
                  />
                </FormGroup>
                {formData.isAccumulative && (
                  <Box sx={{ mt: 2, ml: 4 }}>
                    <TextField
                      label={intl.get('timeOff.newRegimeType.form.accumulativeDays')}
                      type="number"
                      value={formData.accumulativeDays}
                      onChange={handleInputChange('accumulativeDays')}
                      error={!!errors.accumulativeDays}
                      helperText={errors.accumulativeDays}
                      slotProps={{
                        input: {
                        inputProps: {
                            min: 1,
                            max: 999,
                          },
                        },
                      }}
                      sx={{ width: 200 }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid size={12}>
                <Typography variant="h6" sx={{ mb: 1}}>
                  {intl.get('timeOff.newRegimeType.form.workflowStates.title')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {intl.get('timeOff.newRegimeType.form.workflowStates.helper')}
                </Typography>
                <Autocomplete
                  sx={{ mt: 3 }}
                  multiple
                  id="workflow-states"
                  options={workflowStates || []}
                  loading={workflowStatesLoading}
                  value={workflowStates.filter(state =>
                    formData.workflowStateIds.includes(state.id?.toString() || '')
                  )}
                  onChange={handleWorkflowStateChange}
                  getOptionLabel={(state) =>
                    state.labelKey ? intl.get(state.labelKey) : state.name || ''
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={intl.get('timeOff.newRegimeType.form.workflowStates') + '*'}
                      placeholder={intl.get('timeOff.newRegimeType.form.selectWorkflowStates') + '*'}
                      required
                      error={!!errors.workflowStates}
                      helperText={errors.workflowStates}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {workflowStatesLoading ? (
                              <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        label={option.labelKey ? intl.get(option.labelKey) : option.name}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          maxWidth: '200px',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  slots={{
                    popper: Popper
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: true,
                          options: {
                            altBoundary: true,
                            fallbackPlacements: ['bottom'],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            altAxis: true,
                            tether: false,
                          },
                        },
                      ],
                    },
                  }}
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  {intl.get('timeOff.newRegimeType.form.requiredFields')}
                </Typography>
              </Grid>

              <Grid size={12}>
              {createLoading === true ? (
                <Box display={'flex'} justifyContent={'flex-end'} gap={1} my={2}>
                  <Spinner small={true} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                  >
                    <ArrowBackIcon />
                  </Button>
                  <PermissionsGate
                    viewScopes={[SCOPES['timeoff.rrhh']]}
                    editScopes={[SCOPES['timeoff.rrhh']]}
                    editRoles={[ROLES.employeeWriter]}
                    viewRoles={[ROLES.employeeReader]}
                >
                    <Button
                    color="primary"
                    variant="contained"
                    disabled={!isFormValid() || createLoading}
                    onClick={handleSubmit}
                    >
                      <SaveIcon className="mb-1" /> {intl.get('save.button')}
                    </Button>
                </PermissionsGate>
                </Box>
              )}
              </Grid>
            </Grid>
          </Box>
          </PermissionsGate>
        </Box>
    </Box>
  );
};

export default NewRegimeType;
